import { useMemo, useState } from 'react';
import { Box, Dialog, Tooltip, Typography, useTheme } from '@mui/material';
import { Line, LineChart, ResponsiveContainer } from 'recharts';

import DeviceTelemetryChart, { TelemetryPoint } from './DeviceTelemetryChart';

//=================================================================================================

interface DeviceMetricRowProps {
    label: string;
    unit: string;
    value: string;
    color: string;
    points: TelemetryPoint[];
    maxValue?: number;
    minValue?: number;
}

interface SparkEntry {
    ts: number;
    value: number | null;
}

function utcToTs(utcStr: string): number {
    return new Date(utcStr.replace(' ', 'T') + 'Z').getTime();
}

// ─── Мини-график (спарклайн) ─────────────────────────────────────────────────

function Sparkline({
    points,
    color,
    height = 30,
    width = 100,
}: {
    points: TelemetryPoint[];
    color: string;
    height?: number;
    width?: number;
}) {
    const theme = useTheme();

    const data = useMemo<SparkEntry[]>(
        () =>
            [...points]
                .map((p) => ({ ts: utcToTs(p.time), value: p.value }))
                // на случай невалидного времени в точке — такие точки в график не пускаем
                .filter((p) => Number.isFinite(p.ts))
                .sort((a, b) => a.ts - b.ts),
        [points],
    );

    if (!data.length) return null;

    return (
        <Box
            sx={{
                width,
                height,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: theme.palette.background.paper,
                // Отключаем pointer-events, чтобы движения мыши не доходили
                // до внутреннего LineChart (recharts перехватывает их и ломает
                // ховер MUI Tooltip).
                pointerEvents: 'none',
            }}
        >
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
                    <Line
                        type="monotone"
                        dataKey="value"
                        stroke={color}
                        strokeWidth={2}
                        isAnimationActive={false}
                        dot={false}
                        connectNulls={false}
                        color={color}
                    />
                </LineChart>
            </ResponsiveContainer>
        </Box>
    );
}

// ─── Строка метрики с графиком ───────────────────────────────────────────────

export default function DeviceMetricRow({
    label,
    unit,
    value,
    color,
    points,
    maxValue,
    minValue,
}: DeviceMetricRowProps) {
    const theme = useTheme();
    const [open, setOpen] = useState(false);

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    py: 1,
                    minWidth: 0,
                }}
            >
                <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0, width: 260 }}>
                    {label}:
                </Typography>

                <Typography variant="body2" fontWeight={500} sx={{ flexShrink: 0 }}>
                    {value} {unit}
                </Typography>

                <Box sx={{ flex: 1 }} />

                <Tooltip
                    title={
                        <Box sx={{ width: 500 }}>
                            <DeviceTelemetryChart
                                points={points}
                                label={label}
                                unit={unit}
                                color={color}
                                height={220}
                                maxValue={maxValue}
                                minValue={minValue}
                            />
                        </Box>
                    }
                    arrow
                    placement="top"
                    slotProps={{
                        tooltip: {
                            sx: {
                                bgcolor: theme.palette.background.paper,
                                color: theme.palette.text.primary,
                                p: 1,
                                maxWidth: 'none',
                            },
                        },
                    }}
                >
                    <Box
                        sx={{
                            flexShrink: 0,
                            '&:hover': { opacity: 0.9 },
                        }}
                        onClick={() => setOpen(true)}
                    >
                        <Sparkline points={points} color={color} />
                    </Box>
                </Tooltip>
            </Box>

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth onClick={() => setOpen(false)}>
                <Box sx={{ p: 2, cursor: 'zoom-out' }}>
                    <DeviceTelemetryChart
                        points={points}
                        label={label}
                        unit={unit}
                        color={color}
                        height={400}
                        maxValue={maxValue}
                        minValue={minValue}
                    />
                </Box>
            </Dialog>
        </>
    );
}
